/**
 * v4.34.10 — Redemption Investigation Console
 *
 * Compliance-grade forensic surface for investigating every voucher /
 * gift card redemption. Backed by the immutable employee-attribution
 * snapshots emitted via backend/services/redemption_audit.py.
 *
 * Visual posture: closer to Stripe Disputes / Shopify Fraud Review than
 * a generic admin table. Operators should feel "this is a serious
 * accountability surface."
 */
import { useState, useEffect, useCallback, useMemo, useRef, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useAuth } from '../lib/auth';
import { redemptionInvestigationAPI, organizationsAPI, locationsAPI } from '../lib/api';
import { useInvestigationTour } from '../components/InvestigationHelp';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { logger } from '../lib/logger';
import {
    ShieldCheck, ScanSearch, Filter, Download, FileJson, RefreshCw,
    ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Copy, AlertTriangle,
    UserCheck, Receipt, Smartphone, Building2, Globe, Clock, FileText,
    XCircle, CircleSlash, Activity, HelpCircle,
} from 'lucide-react';
import { parseUtc } from '../lib/datetime';

const PAGE_SIZE = 50;
const WARN_LARGE = 10000;
const WARN_HUGE = 50000;
const HARD_CAP = 200000;

const EMPTY_FILTERS = {
    event_type: 'all',
    organization_id: 'all',
    location_id: 'all',
    employee_id: '',
    employee_name: '',
    department: '',
    role: '',
    voucher_code: '',
    gift_card_code: '',
    redemption_source: 'all',
    device_type: 'all',
    platform: 'all',
    start_date: '',
    end_date: '',
};

const ANOMALY_STYLE = {
    legacy_no_snapshot: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
    missing_attribution: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
    manual_override: 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200',
    multi_short_timeframe: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200',
    high_volume: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
};

function buildQueryParams(filters) {
    const out = {};
    Object.entries(filters).forEach(([k, v]) => {
        if (v === '' || v === null || v === undefined || v === 'all') return;
        out[k] = v;
    });
    return out;
}

function copyToClipboard(text, t) {
    try {
        navigator.clipboard.writeText(text);
        toast.success(t('redInv.row.copied'));
    } catch {
        toast.error(t('redInv.export.failed'));
    }
}

function SnapshotBadge({ captured, t }) {
    if (captured) {
        return (
            <span
                data-testid="snapshot-badge-captured"
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"
            >
                <ShieldCheck className="w-3 h-3" />
                {t('redInv.snapshot.captured')}
            </span>
        );
    }
    return (
        <span
            data-testid="snapshot-badge-legacy"
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200"
        >
            <CircleSlash className="w-3 h-3" />
            {t('redInv.snapshot.legacy')}
        </span>
    );
}

function AnomalyBadges({ codes, t }) {
    if (!codes || codes.length === 0) return <span className="text-slate-400 text-xs">—</span>;
    return (
        <div className="flex flex-wrap gap-1">
            {codes.map((code) => (
                <span
                    key={code}
                    data-testid={`anomaly-${code}`}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium ${ANOMALY_STYLE[code] || 'bg-slate-100 text-slate-700'}`}
                    title={t(`redInv.anomaly.${code}`)}
                >
                    <AlertTriangle className="w-3 h-3" />
                    {t(`redInv.anomaly.${code}`)}
                </span>
            ))}
        </div>
    );
}

function Field({ label, value }) {
    return (
        <div className="grid grid-cols-[140px_1fr] gap-2 py-1 text-sm">
            <div className="text-muted-foreground">{label}</div>
            <div className="font-mono text-xs break-all">{value || <span className="text-slate-400">—</span>}</div>
        </div>
    );
}

function ForensicSection({ icon: Icon, title, children }) {
    return (
        <div className="rounded-lg border border-border bg-card/40 p-3">
            <div className="flex items-center gap-2 mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <Icon className="w-3.5 h-3.5" />
                {title}
            </div>
            <div className="space-y-0.5">{children}</div>
        </div>
    );
}

function ExpandedRow({ row, t }) {
    const employeeSnapshot = useMemo(() => ({
        employee_id: row.employee_id,
        employee_name: row.employee_name,
        employee_first_name: row.employee_first_name,
        employee_last_name: row.employee_last_name,
        employee_email: row.employee_email,
        employee_role: row.employee_role,
        employee_department: row.employee_department,
        employee_user_id: row.employee_user_id,
        employee_internal_reference: row.employee_internal_reference,
    }), [row]);

    return (
        <div className="bg-muted/40 px-4 py-4 border-t" data-testid="redinv-row-expanded">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <ForensicSection icon={UserCheck} title={t('redInv.section.identity')}>
                    <Field label="ID" value={row.employee_id} />
                    <Field label="Name" value={row.employee_name} />
                    <Field label="Email" value={row.employee_email} />
                    <Field label="Role" value={row.employee_role} />
                    <Field label="Department" value={row.employee_department} />
                    <Field label="User ID" value={row.employee_user_id} />
                    <Field label="Reference" value={row.employee_internal_reference} />
                </ForensicSection>

                <ForensicSection icon={Clock} title={t('redInv.section.context')}>
                    <Field label="Redeemed at" value={row.redeemed_at} />
                    <Field label="Source" value={row.redemption_source} />
                    <Field label="Session" value={row.redeemed_by_session_id} />
                    <Field label="IP hash" value={row.redeemed_by_ip_hash} />
                    <Field label="Manager" value={row.manager_name_if_applicable || row.manager_id_if_applicable} />
                    <Field label="Notes" value={row.notes} />
                </ForensicSection>

                <ForensicSection icon={Smartphone} title={t('redInv.section.device')}>
                    <Field label="Device" value={row.device_type} />
                    <Field label="Platform" value={row.platform} />
                </ForensicSection>

                <ForensicSection icon={Building2} title={t('redInv.section.org')}>
                    <Field label="Organization" value={row.organization_id} />
                    <Field label="Location" value={row.location_id} />
                </ForensicSection>

                <ForensicSection icon={Receipt} title={t('redInv.section.voucher')}>
                    <Field label="Code" value={row.voucher_code} />
                    <Field label="Serial" value={row.voucher_serial_number} />
                    <Field label="Batch" value={row.voucher_batch_id} />
                    <Field label="ID" value={row.voucher_id} />
                    <Field label="Value" value={row.voucher_value !== '' ? `${row.voucher_value} ${row.currency || ''}` : ''} />
                </ForensicSection>

                <ForensicSection icon={FileText} title={t('redInv.section.audit')}>
                    <Field label="Event" value={row.event_type} />
                    <Field label="Guest session" value={row.related_guest_session_id} />
                    <Field label="Incident" value={row.related_incident_id} />
                </ForensicSection>
            </div>

            <div className="flex flex-wrap gap-2 mt-4">
                <Button
                    variant="outline" size="sm"
                    data-testid="redinv-copy-json"
                    onClick={() => copyToClipboard(JSON.stringify(row, null, 2), t)}
                >
                    <Copy className="w-3.5 h-3.5 mr-1" />
                    {t('redInv.row.copyJson')}
                </Button>
                <Button
                    variant="outline" size="sm"
                    data-testid="redinv-copy-id"
                    onClick={() => copyToClipboard(row.voucher_id || row.voucher_code, t)}
                >
                    <Copy className="w-3.5 h-3.5 mr-1" />
                    {t('redInv.row.copyId')}
                </Button>
                <Button
                    variant="outline" size="sm"
                    data-testid="redinv-copy-snapshot"
                    onClick={() => copyToClipboard(JSON.stringify(employeeSnapshot, null, 2), t)}
                >
                    <Copy className="w-3.5 h-3.5 mr-1" />
                    {t('redInv.row.copySnapshot')}
                </Button>
            </div>
        </div>
    );
}

export default function RedemptionInvestigation() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user, isSuperAdmin, isClientAdmin, isLocationManager } = useAuth();
    const { openHelpCenter, start: startInvTour, active: tourActive } = useInvestigationTour();

    const canAccess = isSuperAdmin() || isClientAdmin() || isLocationManager();
    const canChooseOrg = isSuperAdmin();

    // Auto-start the walkthrough the first time an eligible operator opens
    // the console. Subsequent visits respect the user's choice to dismiss.
    useEffect(() => {
        if (!canAccess || tourActive) return;
        const seenKey = user?.id ? `yulontap_inv_tour_seen_${user.id}` : null;
        if (!seenKey) return;
        try {
            if (!localStorage.getItem(seenKey)) {
                const timer = setTimeout(() => startInvTour(), 600);
                return () => clearTimeout(timer);
            }
        } catch { /* storage unavailable */ }
    }, [canAccess, user?.id, startInvTour, tourActive]);

    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
    const [rows, setRows] = useState([]);
    const [stats, setStats] = useState({ total: 0, vouchers: 0, gift_cards: 0, unique_employees: 0 });
    const [loading, setLoading] = useState(false);
    const [errorState, setErrorState] = useState(false);
    const [page, setPage] = useState(0);
    const [expanded, setExpanded] = useState({});

    const [organizations, setOrganizations] = useState([]);
    const [locations, setLocations] = useState([]);

    const [exportBusy, setExportBusy] = useState(false);
    const [exportDialog, setExportDialog] = useState({ open: false, format: null, count: 0, tier: null });
    const sessionGeneratedAt = useRef(new Date());

    useEffect(() => {
        if (!canAccess) return;
        if (canChooseOrg) {
            organizationsAPI.list()
                .then((r) => setOrganizations(r.data || []))
                .catch((err) => logger.warn('redInv: organizations load failed', err));
        }
        locationsAPI.list()
            .then((r) => setLocations(r.data || []))
            .catch((err) => logger.warn('redInv: locations load failed', err));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canAccess, canChooseOrg]);

    const fetchPage = useCallback(async (filtersToUse, pageNum) => {
        setLoading(true);
        setErrorState(false);
        try {
            const params = { ...buildQueryParams(filtersToUse), skip: pageNum * PAGE_SIZE, limit: PAGE_SIZE };
            const [listRes, statsRes] = await Promise.all([
                redemptionInvestigationAPI.list(params),
                redemptionInvestigationAPI.stats(buildQueryParams(filtersToUse)),
            ]);
            setRows(listRes.data.rows || []);
            setStats(statsRes.data || { total: 0, vouchers: 0, gift_cards: 0, unique_employees: 0 });
        } catch (err) {
            console.error('redInv: fetch failed', err);
            setErrorState(true);
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!canAccess) return;
        fetchPage(appliedFilters, page);
    }, [canAccess, appliedFilters, page, fetchPage]);

    const applyFilters = () => {
        setPage(0);
        setAppliedFilters(filters);
    };

    const clearFilters = () => {
        setFilters(EMPTY_FILTERS);
        setAppliedFilters(EMPTY_FILTERS);
        setPage(0);
    };

    const activeFilterDescription = useMemo(() => {
        const entries = Object.entries(appliedFilters)
            .filter(([, v]) => v && v !== 'all')
            .map(([k, v]) => `${k}=${v}`);
        return entries.length === 0 ? t('redInv.session.noFilters') : entries.join(' · ');
    }, [appliedFilters, t]);

    const orgScope = canChooseOrg
        ? (appliedFilters.organization_id === 'all' ? t('redInv.filters.allOrgs') : appliedFilters.organization_id)
        : (user?.organization_id || 'scoped');

    const beginExport = (format) => {
        const count = stats.total || 0;
        let tier = 'normal';
        if (count >= HARD_CAP) tier = 'cap';
        else if (count >= WARN_HUGE) tier = 'huge';
        else if (count >= WARN_LARGE) tier = 'large';
        if (tier === 'normal') {
            // Direct export with no confirmation.
            runExport(format, count);
            return;
        }
        setExportDialog({ open: true, format, count, tier });
    };

    const runExport = async (formatType, count) => {
        if (exportBusy) return; // prevent double-click
        setExportBusy(true);
        try {
            const params = { ...buildQueryParams(appliedFilters), format: formatType };
            const res = await redemptionInvestigationAPI.exportData(params);
            const blob = new Blob([res.data], {
                type: formatType === 'csv' ? 'text/csv;charset=utf-8' : 'application/json',
            });
            // Filename is governed by backend Content-Disposition; we still
            // provide a sensible fallback in case the browser drops it.
            const disposition = res.headers?.['content-disposition'] || '';
            const match = disposition.match(/filename="?([^";]+)"?/);
            const fallback = `redemption-investigation_${new Date().toISOString().slice(0, 10)}.${formatType}`;
            const filename = match ? match[1] : fallback;
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success(t('redInv.export.done'));
        } catch (err) {
            console.error('redInv: export failed', err);
            toast.error(t('redInv.export.failed'));
        } finally {
            setExportBusy(false);
            setExportDialog({ open: false, format: null, count: 0, tier: null });
        }
        // Reference count for analytics — suppresses unused-arg lint.
        void count;
    };

    const totalPages = Math.max(1, Math.ceil((stats.total || 0) / PAGE_SIZE));

    if (!canAccess) {
        return (
            <div className="py-12 text-center" data-testid="redinv-access-denied">
                <ShieldCheck className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                <h2 className="text-lg font-semibold">{t('redInv.access.denied.title')}</h2>
                <p className="text-sm text-muted-foreground mt-2">{t('redInv.access.denied.body')}</p>
                <Button className="mt-4" onClick={() => navigate('/')}>Dashboard</Button>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="redinv-page">
            {/* Investigation session header — chain-of-custody record */}
            <Card className="border-2 border-slate-300 dark:border-slate-700 bg-gradient-to-br from-slate-50 to-white dark:from-slate-900 dark:to-slate-950">
                <CardContent className="pt-6 pb-5">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <ScanSearch className="w-5 h-5 text-rose-600" />
                                <h1 className="text-2xl font-bold tracking-tight" data-testid="redinv-title">
                                    {t('redInv.title')}
                                </h1>
                                <Badge variant="outline" className="border-rose-500 text-rose-700 dark:text-rose-300 ml-1">
                                    {t('redInv.headerBadge')}
                                </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground max-w-3xl">{t('redInv.subtitle')}</p>
                        </div>
                        <div className="flex gap-2 shrink-0">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={openHelpCenter}
                                title={t('redInv.help.tooltip')}
                                data-testid="redinv-help-button"
                            >
                                <HelpCircle className="w-4 h-4 mr-1" />
                                {t('redInv.help.button')}
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => fetchPage(appliedFilters, page)}
                                data-testid="redinv-refresh"
                            >
                                <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                                Refresh
                            </Button>
                            <Button
                                variant="default"
                                size="sm"
                                onClick={() => beginExport('csv')}
                                disabled={exportBusy || (stats.total || 0) === 0}
                                data-testid="redinv-export-csv"
                            >
                                <Download className="w-4 h-4 mr-1" />
                                {t('redInv.export.csv')}
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => beginExport('json')}
                                disabled={exportBusy || (stats.total || 0) === 0}
                                data-testid="redinv-export-json"
                            >
                                <FileJson className="w-4 h-4 mr-1" />
                                {t('redInv.export.json')}
                            </Button>
                        </div>
                    </div>

                    <div className="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
                        <div className="rounded border border-border bg-background/60 p-2">
                            <div className="text-muted-foreground">{t('redInv.session.generated')}</div>
                            <div className="font-mono mt-0.5" data-testid="redinv-session-generated">
                                {format(sessionGeneratedAt.current, 'yyyy-MM-dd HH:mm:ss')}
                            </div>
                        </div>
                        <div className="rounded border border-border bg-background/60 p-2">
                            <div className="text-muted-foreground">{t('redInv.session.requestedBy')}</div>
                            <div className="font-mono mt-0.5 truncate" title={user?.email}>{user?.email || '—'}</div>
                        </div>
                        <div className="rounded border border-border bg-background/60 p-2">
                            <div className="text-muted-foreground">{t('redInv.session.orgScope')}</div>
                            <div className="font-mono mt-0.5 truncate" data-testid="redinv-session-orgscope">{orgScope}</div>
                        </div>
                        <div className="rounded border border-border bg-background/60 p-2">
                            <div className="text-muted-foreground">{t('redInv.session.totalMatched')}</div>
                            <div className="font-mono mt-0.5" data-testid="redinv-session-total">{stats.total}</div>
                        </div>
                        <div className="rounded border border-border bg-background/60 p-2 md:col-span-2 lg:col-span-4">
                            <div className="text-muted-foreground">{t('redInv.session.filters')}</div>
                            <div className="font-mono mt-0.5 break-all text-[11px]">{activeFilterDescription}</div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Stat cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card data-testid="redinv-stat-total">
                    <CardContent className="pt-4">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                            <Activity className="w-4 h-4" />
                            {t('redInv.stats.total')}
                        </div>
                        <div className="text-2xl font-bold">{stats.total}</div>
                    </CardContent>
                </Card>
                <Card data-testid="redinv-stat-vouchers">
                    <CardContent className="pt-4">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                            <Receipt className="w-4 h-4 text-emerald-600" />
                            {t('redInv.stats.vouchers')}
                        </div>
                        <div className="text-2xl font-bold text-emerald-600">{stats.vouchers}</div>
                    </CardContent>
                </Card>
                <Card data-testid="redinv-stat-giftcards">
                    <CardContent className="pt-4">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                            <Globe className="w-4 h-4 text-violet-600" />
                            {t('redInv.stats.giftCards')}
                        </div>
                        <div className="text-2xl font-bold text-violet-600">{stats.gift_cards}</div>
                    </CardContent>
                </Card>
                <Card data-testid="redinv-stat-employees">
                    <CardContent className="pt-4">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                            <UserCheck className="w-4 h-4 text-blue-600" />
                            {t('redInv.stats.uniqueEmployees')}
                        </div>
                        <div className="text-2xl font-bold text-blue-600">{stats.unique_employees}</div>
                    </CardContent>
                </Card>
            </div>

            {/* Filter panel */}
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                        <Filter className="w-4 h-4" />
                        {t('redInv.filters.title')}
                    </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div>
                        <label className="text-xs text-muted-foreground">{t('redInv.filters.dateFrom')}</label>
                        <Input
                            type="date"
                            value={filters.start_date}
                            onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                            data-testid="redinv-filter-start"
                        />
                    </div>
                    <div>
                        <label className="text-xs text-muted-foreground">{t('redInv.filters.dateTo')}</label>
                        <Input
                            type="date"
                            value={filters.end_date}
                            onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                            data-testid="redinv-filter-end"
                        />
                    </div>
                    <div>
                        <label className="text-xs text-muted-foreground">{t('redInv.filters.eventType')}</label>
                        <Select value={filters.event_type} onValueChange={(v) => setFilters({ ...filters, event_type: v })}>
                            <SelectTrigger data-testid="redinv-filter-event"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">{t('redInv.filters.all')}</SelectItem>
                                <SelectItem value="voucher_redeemed">{t('redInv.eventType.voucher')}</SelectItem>
                                <SelectItem value="gift_card_redeem">{t('redInv.eventType.giftCard')}</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    {canChooseOrg && (
                        <div>
                            <label className="text-xs text-muted-foreground">{t('redInv.filters.organization')}</label>
                            <Select value={filters.organization_id} onValueChange={(v) => setFilters({ ...filters, organization_id: v })}>
                                <SelectTrigger data-testid="redinv-filter-org"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('redInv.filters.allOrgs')}</SelectItem>
                                    {organizations.map((o) => (
                                        <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    <div>
                        <label className="text-xs text-muted-foreground">{t('redInv.filters.location')}</label>
                        <Select value={filters.location_id} onValueChange={(v) => setFilters({ ...filters, location_id: v })}>
                            <SelectTrigger data-testid="redinv-filter-location"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">{t('redInv.filters.allLocations')}</SelectItem>
                                {locations.map((l) => (
                                    <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-xs text-muted-foreground">{t('redInv.filters.employeeName')}</label>
                        <Input
                            placeholder="Alice…"
                            value={filters.employee_name}
                            onChange={(e) => setFilters({ ...filters, employee_name: e.target.value })}
                            data-testid="redinv-filter-employee-name"
                        />
                    </div>
                    <div>
                        <label className="text-xs text-muted-foreground">{t('redInv.filters.employeeId')}</label>
                        <Input
                            placeholder="emp-…"
                            value={filters.employee_id}
                            onChange={(e) => setFilters({ ...filters, employee_id: e.target.value })}
                            data-testid="redinv-filter-employee-id"
                        />
                    </div>
                    <div>
                        <label className="text-xs text-muted-foreground">{t('redInv.filters.department')}</label>
                        <Input
                            value={filters.department}
                            onChange={(e) => setFilters({ ...filters, department: e.target.value })}
                            data-testid="redinv-filter-department"
                        />
                    </div>
                    <div>
                        <label className="text-xs text-muted-foreground">{t('redInv.filters.role')}</label>
                        <Input
                            value={filters.role}
                            onChange={(e) => setFilters({ ...filters, role: e.target.value })}
                            data-testid="redinv-filter-role"
                        />
                    </div>
                    <div>
                        <label className="text-xs text-muted-foreground">{t('redInv.filters.voucherCode')}</label>
                        <Input
                            value={filters.voucher_code}
                            onChange={(e) => setFilters({ ...filters, voucher_code: e.target.value })}
                            data-testid="redinv-filter-voucher"
                        />
                    </div>
                    <div>
                        <label className="text-xs text-muted-foreground">{t('redInv.filters.giftCardCode')}</label>
                        <Input
                            value={filters.gift_card_code}
                            onChange={(e) => setFilters({ ...filters, gift_card_code: e.target.value })}
                            data-testid="redinv-filter-giftcard"
                        />
                    </div>
                    <div>
                        <label className="text-xs text-muted-foreground">{t('redInv.filters.device')}</label>
                        <Select value={filters.device_type} onValueChange={(v) => setFilters({ ...filters, device_type: v })}>
                            <SelectTrigger data-testid="redinv-filter-device"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">{t('redInv.filters.all')}</SelectItem>
                                <SelectItem value="phone">phone</SelectItem>
                                <SelectItem value="tablet">tablet</SelectItem>
                                <SelectItem value="desktop">desktop</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-xs text-muted-foreground">{t('redInv.filters.platform')}</label>
                        <Select value={filters.platform} onValueChange={(v) => setFilters({ ...filters, platform: v })}>
                            <SelectTrigger data-testid="redinv-filter-platform"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">{t('redInv.filters.all')}</SelectItem>
                                <SelectItem value="ios">iOS</SelectItem>
                                <SelectItem value="android">Android</SelectItem>
                                <SelectItem value="desktop">Desktop</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="sm:col-span-2 lg:col-span-4 flex flex-wrap items-center justify-end gap-2 pt-2">
                        <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="redinv-filters-clear">
                            <XCircle className="w-4 h-4 mr-1" />
                            {t('redInv.filters.clear')}
                        </Button>
                        <Button size="sm" onClick={applyFilters} data-testid="redinv-filters-apply">
                            {t('redInv.filters.apply')}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Results table */}
            <Card>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="py-16 text-center text-muted-foreground" data-testid="redinv-loading">
                            <RefreshCw className="w-6 h-6 mx-auto animate-spin opacity-60" />
                            <div className="text-sm mt-2">{t('redInv.table.loading')}</div>
                        </div>
                    ) : errorState ? (
                        <div className="py-16 text-center" data-testid="redinv-error">
                            <AlertTriangle className="w-8 h-8 mx-auto text-rose-500" />
                            <div className="text-sm mt-2 text-muted-foreground">{t('redInv.table.error')}</div>
                            <Button variant="outline" size="sm" className="mt-3" onClick={() => fetchPage(appliedFilters, page)}>
                                Retry
                            </Button>
                        </div>
                    ) : rows.length === 0 ? (
                        <div className="py-16 text-center" data-testid="redinv-empty">
                            <ScanSearch className="w-10 h-10 mx-auto opacity-30" />
                            <p className="text-sm text-muted-foreground mt-2">{t('redInv.table.empty')}</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                                    <tr>
                                        <th className="px-3 py-2 text-left w-8"></th>
                                        <th className="px-3 py-2 text-left">{t('redInv.table.timestamp')}</th>
                                        <th className="px-3 py-2 text-left">{t('redInv.table.event')}</th>
                                        <th className="px-3 py-2 text-left">{t('redInv.table.employee')}</th>
                                        <th className="px-3 py-2 text-left">{t('redInv.table.voucher')}</th>
                                        <th className="px-3 py-2 text-right">{t('redInv.table.value')}</th>
                                        <th className="px-3 py-2 text-left">{t('redInv.table.source')}</th>
                                        <th className="px-3 py-2 text-left">{t('redInv.table.flags')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row, idx) => {
                                        const key = `${row.timestamp}-${row.voucher_id || row.voucher_code || idx}`;
                                        const isOpen = !!expanded[key];
                                        return (
                                            <Fragment key={key}>
                                                <tr
                                                    className="border-t border-border hover:bg-muted/30 cursor-pointer"
                                                    onClick={() => setExpanded({ ...expanded, [key]: !isOpen })}
                                                    data-testid={`redinv-row-${idx}`}
                                                >
                                                    <td className="px-3 py-2">
                                                        {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                                    </td>
                                                    <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">
                                                        {row.timestamp ? format(parseUtc(row.timestamp), 'yyyy-MM-dd HH:mm:ss') : '—'}
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        <Badge variant="outline" className="text-[11px]">
                                                            {row.event_type === 'voucher_redeemed'
                                                                ? t('redInv.eventType.voucher')
                                                                : t('redInv.eventType.giftCard')}
                                                        </Badge>
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        <div className="font-medium">{row.employee_name || row.employee_email || '—'}</div>
                                                        <div className="mt-1">
                                                            <SnapshotBadge captured={row._snapshot_captured} t={t} />
                                                        </div>
                                                    </td>
                                                    <td className="px-3 py-2 font-mono text-xs">{row.voucher_code || '—'}</td>
                                                    <td className="px-3 py-2 text-right font-mono text-xs">
                                                        {row.voucher_value !== '' ? `${row.voucher_value} ${row.currency || ''}` : '—'}
                                                    </td>
                                                    <td className="px-3 py-2 text-xs">{row.redemption_source || '—'}</td>
                                                    <td className="px-3 py-2">
                                                        <AnomalyBadges codes={row._anomalies} t={t} />
                                                    </td>
                                                </tr>
                                                {isOpen && (
                                                    <tr>
                                                        <td colSpan={8} className="p-0">
                                                            <ExpandedRow row={row} t={t} />
                                                        </td>
                                                    </tr>
                                                )}
                                            </Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Pagination */}
                    {rows.length > 0 && (
                        <div className="flex items-center justify-between p-3 border-t">
                            <div className="text-xs text-muted-foreground" data-testid="redinv-pagination-label">
                                {t('redInv.pagination.showing', {
                                    from: page * PAGE_SIZE + 1,
                                    to: Math.min((page + 1) * PAGE_SIZE, stats.total),
                                    total: stats.total,
                                })}
                            </div>
                            <div className="flex items-center gap-2">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={page === 0}
                                    onClick={() => setPage(page - 1)}
                                    data-testid="redinv-page-prev"
                                >
                                    <ChevronLeft className="w-4 h-4 mr-1" />
                                    {t('redInv.pagination.prev')}
                                </Button>
                                <div className="text-xs px-2">{page + 1} / {totalPages}</div>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={page + 1 >= totalPages}
                                    onClick={() => setPage(page + 1)}
                                    data-testid="redinv-page-next"
                                >
                                    {t('redInv.pagination.next')}
                                    <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Export dialog with tiered warnings */}
            <Dialog open={exportDialog.open} onOpenChange={(o) => !o && setExportDialog({ ...exportDialog, open: false })}>
                <DialogContent data-testid="redinv-export-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            {exportDialog.tier === 'cap' && <CircleSlash className="w-5 h-5 text-rose-600" />}
                            {exportDialog.tier === 'huge' && <AlertTriangle className="w-5 h-5 text-orange-600" />}
                            {exportDialog.tier === 'large' && <AlertTriangle className="w-5 h-5 text-amber-600" />}
                            {exportDialog.tier === 'cap' && t('redInv.export.warnCap.title')}
                            {exportDialog.tier === 'huge' && t('redInv.export.warnHuge.title')}
                            {exportDialog.tier === 'large' && t('redInv.export.warnLarge.title')}
                        </DialogTitle>
                        <DialogDescription>
                            {exportDialog.tier === 'cap' && t('redInv.export.warnCap.body', { count: exportDialog.count })}
                            {exportDialog.tier === 'huge' && t('redInv.export.warnHuge.body', { count: exportDialog.count })}
                            {exportDialog.tier === 'large' && t('redInv.export.warnLarge.body', { count: exportDialog.count })}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setExportDialog({ ...exportDialog, open: false })}
                            data-testid="redinv-export-cancel"
                        >
                            {t('redInv.export.cancel')}
                        </Button>
                        {exportDialog.tier !== 'cap' && (
                            <Button
                                onClick={() => runExport(exportDialog.format, exportDialog.count)}
                                disabled={exportBusy}
                                data-testid="redinv-export-confirm"
                            >
                                {exportBusy ? t('redInv.export.preparing') : t('redInv.export.confirm')}
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
